import { useState, useRef, useEffect } from 'react';
import { useOnboarding } from '../OnboardingContext';
import { Calendar, FileCheck, AlertCircle, ShieldX } from 'lucide-react';
import { useScrollToActivate } from '@/hooks/useScrollToActivate';

export function DateOfBirthStep() {
  const { data, updateData, goToNextStep } = useOnboarding();
  const { canActivate } = useScrollToActivate();
  const [isMinor, setIsMinor] = useState(false);
  
  // Initialize from saved data or extracted data
  const getInitialValues = () => {
    const dateStr = data.dateOfBirth || data.extractedDateOfBirth;
    if (dateStr) {
      const [y, m, d] = dateStr.split('-');
      return { day: d || '', month: m || '', year: y || '' };
    }
    return { day: '', month: '', year: '' };
  };
  
  const initial = getInitialValues();
  const [day, setDay] = useState(initial.day);
  const [month, setMonth] = useState(initial.month);
  const [year, setYear] = useState(initial.year);
  
  const monthRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);

  const hasExtractedData = !!data.extractedDateOfBirth;
  
  const getCurrentValue = () => {
    if (day && month && year) {
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return '';
  };
  
  const isModified = hasExtractedData && getCurrentValue() && getCurrentValue() !== data.extractedDateOfBirth;

  // Check if the person is under 18
  const checkIsMinor = (d: number, m: number, y: number): boolean => {
    const today = new Date();
    const birthDate = new Date(y, m - 1, d);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age < 18;
  };

  const isValid = () => {
    const d = parseInt(day);
    const m = parseInt(month);
    const y = parseInt(year);
    
    if (isNaN(d) || isNaN(m) || isNaN(y)) return false;
    if (d < 1 || d > 31) return false;
    if (m < 1 || m > 12) return false;
    if (y < 1900 || y > new Date().getFullYear()) return false;
    
    return true;
  };

  // Update minor check whenever date changes
  useEffect(() => {
    if (isValid()) {
      const d = parseInt(day);
      const m = parseInt(month);
      const y = parseInt(year);
      setIsMinor(checkIsMinor(d, m, y));
    } else {
      setIsMinor(false);
    }
  }, [day, month, year]);

  const handleContinue = () => {
    if (isValid() && !isMinor) {
      updateData({ dateOfBirth: `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}` });
      goToNextStep();
    }
  };

  const formatInput = (value: string, max: number) => {
    const numbers = value.replace(/\D/g, '');
    return numbers.slice(0, max);
  };

  const handleDayChange = (value: string) => {
    const formatted = formatInput(value, 2);
    setDay(formatted);
    if (formatted.length === 2) {
      monthRef.current?.focus();
    }
  };

  const handleMonthChange = (value: string) => {
    const formatted = formatInput(value, 2);
    setMonth(formatted);
    if (formatted.length === 2) {
      yearRef.current?.focus();
    }
  };

  const handleYearChange = (value: string) => {
    const formatted = formatInput(value, 4);
    setYear(formatted);
  };

  // Minor rejection screen
  if (isMinor) {
    return (
      <div className="min-h-screen bg-background flex flex-col pt-24 pb-28 px-4 sm:px-6">
        <div className="flex-1 flex flex-col items-center justify-center max-w-md mx-auto w-full text-center">
          <div className="w-20 h-20 rounded-full bg-destructive/10 flex items-center justify-center mb-6">
            <ShieldX className="w-10 h-10 text-destructive" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-3">
            Unable to proceed
          </h1>
          <p className="text-muted-foreground mb-6">
            We are unable to offer our services to individuals under the age of 18. This is a regulatory requirement we must comply with.
          </p>
          <div className="w-full p-4 rounded-xl bg-destructive/5 border border-destructive/20">
            <p className="text-sm text-muted-foreground">
              If you believe this is an error, please check your date of birth and try again.
            </p>
          </div>
          <button
            onClick={() => {
              setDay('');
              setMonth('');
              setYear('');
              setIsMinor(false);
            }}
            className="mt-6 px-6 py-3 text-sm font-medium rounded-xl border border-border text-foreground hover:bg-secondary transition-colors"
          >
            Re-enter date of birth
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col pt-24 pb-28 px-4 sm:px-6">
      <div className="flex-1 flex flex-col max-w-md mx-auto w-full overflow-y-auto">
        <div className="mb-6">
          <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mb-6">
            <Calendar className="w-8 h-8 text-accent" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">
            Confirm date of birth
          </h1>
          <p className="text-muted-foreground">
            {hasExtractedData 
              ? "We've extracted your date of birth from your ID. Please confirm or correct if needed."
              : "Enter your date of birth as it appears on your ID document."
            }
          </p>
        </div>

        {hasExtractedData && (
          <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-accent/10 border border-accent/20 mb-6">
            <FileCheck className="w-5 h-5 text-accent shrink-0" />
            <p className="text-sm text-foreground">
              Pre-filled from your ID document
            </p>
          </div>
        )}

        <div className="flex gap-3">
          <div className="flex-1">
            <label className="block text-sm font-medium text-foreground mb-2">
              Day
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={day}
              onChange={(e) => handleDayChange(e.target.value)}
              placeholder="DD"
              className="input-field text-center"
              maxLength={2}
              autoFocus
            />
          </div>

          <div className="flex-1">
            <label className="block text-sm font-medium text-foreground mb-2">
              Month
            </label>
            <input
              ref={monthRef}
              type="text"
              inputMode="numeric"
              value={month}
              onChange={(e) => handleMonthChange(e.target.value)}
              placeholder="MM"
              className="input-field text-center"
              maxLength={2}
            />
          </div>

          <div className="flex-[1.5]">
            <label className="block text-sm font-medium text-foreground mb-2">
              Year
            </label>
            <input
              ref={yearRef}
              type="text"
              inputMode="numeric"
              value={year}
              onChange={(e) => handleYearChange(e.target.value)}
              placeholder="YYYY"
              className="input-field text-center"
              maxLength={4}
            />
          </div>
        </div>

      </div>
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t border-border">
        <div className="max-w-md mx-auto">
          <button
            onClick={handleContinue}
            disabled={!isValid() || isMinor || !canActivate}
            className="w-full h-14 text-lg font-semibold rounded-xl bg-accent hover:bg-accent/90 text-accent-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {hasExtractedData ? 'Confirm & Continue' : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}
